import { useState, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { Grid } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';

// Our Components
import FinancialGoalDropdown from 'components/Client/Onboarding/FinancialGoalDropdown';
import LiabilityFormHeading from 'components/Client/Onboarding/LiabilityFormHeading';
import LiabilityDynamicButton from 'components/Client/Onboarding/LiabilityDynamicButton';
import Loader from 'components/Loader';
import LoanTypeDropdown from 'components/Client/Onboarding/LoanTypeDropdown';
import MonthlyPaymentInput from 'components/Client/Onboarding/MonthlyPaymentInput';
import OutstandingBalanceInput from 'components/Client/Onboarding/OutstandingBalanceInput';
import RateInput from 'components/Input/RateInput';
import StandardDatePicker from 'components/DatePicker/StandardDatePicker';
import TextInput from 'components/Input/TextInput';
import TenureDropdown from 'components/Client/Onboarding/TenureDropdown';

// Input Types
import { TEXT } from 'components/Input/Types';

// Utils
import {
	dataIsValid,
	isSubmissionReady,
	roundLiabilityRate
} from 'shared/utils';
import formatLoanType from 'shared/utils/formatting/liability/formatLoanType';
import dateToIsoFormat from 'shared/utils/clientOnboarding/dateToIsoFormat';
import FINANCIAL_GOAL_TO_ENUM_MAPPING from 'shared/utils/clientOnboarding/financialGoalMapping';
import shouldUpdateRateManuallyUpdated from 'shared/utils/events/shouldUpdateRateManuallyUpdated';
import getDefaultApproxPayoffDate from 'shared/utils/clientOnboarding/getDefaultApproxPayoffDate';
import isAutomatedRun from 'shared/utils/events/isAutomatedRun';

// Our Hooks
import useGetUserPersonalLiability from 'hooks/clientOnboarding/useGetUserPersonalLiability';
import useMutateSavePersonalLiability from 'hooks/clientOnboarding/mutations/useSavePersonalLiability';
import usePrepopulateState from 'hooks/prepopulate/usePrepopulateState';
import useDisableOutstandingBalanceBasedOnSource from 'hooks/liability/formValidation/useDisableOutstandingBalanceBasedOnSource';

// Constants
import { PERSONAL } from 'shared/constants';

function PersonalLiability() {
	const navigate = useNavigate();
	const savePersonalLoan = useMutateSavePersonalLiability();
	const { isLoading, isSuccess, data } = useGetUserPersonalLiability();

	const { isLoading: isMutationLoading } = savePersonalLoan;

	// Personal Liability Related
	const [approxPayoffDate, setApproxPayoffDate] = useState(
		getDefaultApproxPayoffDate(PERSONAL)
	);
	const [lender, setLender] = useState('');
	const [loanType, setLoanType] = useState('Fixed');
	const [monthlyPayment, setMonthlyPayment] = useState('');
	const [ownershipTenure, setOwnershipTenure] = useState('');
	const [outstandingBalance, setOutstandingBalance] = useState(''); // balance / 100 is bc methodfi provides balance in cents so we divide by 100 to convert into dollars
	const [rate, setRate] = useState('');

	// Personal Related Data
	const [loanPurpose, setLoanPurpose] = useState('');

	// Financial Goal
	const [financialGoal, setFinancialGoal] = useState(
		'Reduce total cost of debt'
	);

	const disableOutstandingBalanceBasedOnSource =
		useDisableOutstandingBalanceBasedOnSource(data);

	const didRateChange = useMemo(() => {
		// if data is empty false
		if (!dataIsValid(data)) return false;
		// if interestRate is empty false.
		if (!dataIsValid(data?.interestRate)) return false;
		// if rate is empty false.
		if (rate === '') return false;
		// compare NOW
		return +rate !== data.interestRate;
	}, [rate, data]);

	usePrepopulateState(
		isSuccess
			? [
					{
						setState: setOutstandingBalance,
						value: data?.outstandingBalance
					},
					{
						setState: setRate,
						value: `${
							data?.interestRate
								? roundLiabilityRate(data?.interestRate)
								: ''
						}`
					},
					{
						setState: setApproxPayoffDate,
						value: data?.expectedPayOffDate
					},
					{ setState: setLender, value: data?.lender },
					{ setState: setMonthlyPayment, value: data?.monthlyPay },
					{
						setState: setOwnershipTenure,
						value: Math.floor(data?.tenureMonth ?? 0) / 12
					},
					{ setState: setLoanType, value: data?.loanType },
					{ setState: setLoanPurpose, value: data?.loanUserFor },
					{ setState: setFinancialGoal, value: data?.financialGoal },
					{
						setState: setLoanType,
						value: formatLoanType(data?.loanType ?? '')
					}
			  ]
			: [],
		[data]
	);

	const requiredFormValues = [
		approxPayoffDate,
		financialGoal,
		lender,
		monthlyPayment,
		outstandingBalance,
		ownershipTenure,
		rate
	];

	const isFormReady = useMemo(
		() =>
			isAutomatedRun()
				? true
				: isSubmissionReady(requiredFormValues) &&
				  approxPayoffDate !== null,
		requiredFormValues
	);

	const handleApproxPayoffDateChange = (value) => {
		const isValueEmpty = value === '';
		if (isValueEmpty) {
			setApproxPayoffDate('');
			return;
		}
		setApproxPayoffDate(value);
	};

	const paramsFromURL = useParams();
	const clientId = paramsFromURL?.clientId;

	const submitLiabilityData = (route) => {
		const formattedApproxPayOffDate = dateToIsoFormat(approxPayoffDate);
		const formattedFinancialGoal =
			FINANCIAL_GOAL_TO_ENUM_MAPPING(financialGoal);
		const formattedLoanType = loanType.toUpperCase();
		const formattedRate = roundLiabilityRate(+rate);

		const personalLoan = {
			clientId,
			approxPayoffDate: formattedApproxPayOffDate,
			financialGoal: formattedFinancialGoal,
			lender,
			loanType: formattedLoanType,
			personalLoanUse: loanPurpose,
			monthlyPayment: +monthlyPayment,
			outstandingBalance: +outstandingBalance,
			rate: formattedRate,
			tenure: +ownershipTenure,
			tradelineType: PERSONAL // can be one of AUTO | MORTGAGE | PERSONAL | STUDENT
		};

		personalLoan.rateManuallyUpdated = shouldUpdateRateManuallyUpdated(
			data?.rateManuallyUpdated,
			didRateChange
		);

		savePersonalLoan.mutate(personalLoan, {
			onSuccess: () => {
				navigate(route);
			}
		});
	};

	if (isLoading) {
		return (
			<>
				<Helmet>
					<title>Personal Loan</title>
				</Helmet>
				{/* Once data binding begins we will add Mortgage specific information to this header */}
				<LiabilityFormHeading headingText="Personal Loan" />
				<Loader
					size={60}
					boxSX={{ alignItems: 'center', marginTop: 15 }}
				/>
			</>
		);
	}
	return (
		<>
			<Helmet>
				<title>Personal Loan</title>
			</Helmet>

			{/* Once data binding begins we will add Mortgage specific information to this header */}
			<LiabilityFormHeading headingText="Personal Loan" />

			<Grid
				component="form"
				container
				noValidate
				columnSpacing={2}
				rowSpacing={3}
				autoComplete="off"
				marginTop={4}
			>
				<Grid item xs={6} sx={{ height: 120 }}>
					<OutstandingBalanceInput
						outstandingBalance={outstandingBalance}
						setOutstandingBalance={setOutstandingBalance}
						disabled={disableOutstandingBalanceBasedOnSource}
					/>
				</Grid>
				<Grid item xs={6} sx={{ height: 120 }}>
					<MonthlyPaymentInput
						monthlyPayment={monthlyPayment}
						outstandingBalance={outstandingBalance}
						setMonthlyPayment={setMonthlyPayment}
					/>
				</Grid>
				<Grid item xs={6} sx={{ height: 120 }}>
					<RateInput rate={rate} setRate={setRate} />
				</Grid>
				<Grid item xs={6} sx={{ height: 120 }}>
					<StandardDatePicker
						label="Expected Payoff Date"
						helperText="Enter the approximate payoff date"
						onChange={handleApproxPayoffDateChange}
						value={approxPayoffDate}
						error={approxPayoffDate === ''}
					/>
				</Grid>

				<Grid item xs={6}>
					<TenureDropdown
						tenure={ownershipTenure}
						setTenure={setOwnershipTenure}
						dropDownSX={{ width: '100%' }}
					/>
				</Grid>

				{/* Takes 5 Grid Columns */}
				<LoanTypeDropdown
					gridColumns={6}
					loanType={loanType}
					setLoanType={setLoanType}
				/>

				<Grid item xs={6} sx={{ height: 120 }}>
					<TextInput
						type={TEXT}
						label="Lender"
						subLabel="Enter the name of your lender"
						value={lender}
						onChange={setLender}
					/>
				</Grid>

				<FinancialGoalDropdown
					gridColumns={6}
					withoutCashout
					financialGoal={financialGoal}
					setFinancialGoal={setFinancialGoal}
					dropDownLabelSX={{ whiteSpace: 'nowrap' }}
				/>

				<Grid item xs={12}>
					<LiabilityDynamicButton
						disabled={!isFormReady}
						isMutationLoading={isMutationLoading}
						onClick={submitLiabilityData}
					/>
				</Grid>
			</Grid>
		</>
	);
}

export default PersonalLiability;
